import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SUBMENU_CLOSE_DELAY = 150;
const DEFAULT_SUBMENU_ANIMATION_DURATION = 360;
const SUBMENU_ANIMATION_CLEANUP_BUFFER = 20;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #submenuMutationObserverTimer;

  /**
   * Keeps the active submenu height in sync when hydrated content or images change its layout.
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * The submenu that is finishing its closing transition.
   * @type {HTMLElement | null}
   */
  #closingSubmenu = null;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeAnimationTimer;

  /**
   * Keeps hover-accordion panels from shrinking under the pointer during one open session.
   * @type {number}
   */
  #activeSubmenuMaxHeight = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('focusin', this.#submenuHeightChangeListener);
    this.addEventListener('focusout', this.#submenuHeightChangeListener);
    this.addEventListener('pointerover', this.#submenuHeightChangeListener);
    this.addEventListener('pointerout', this.#submenuHeightChangeListener);
    this.addEventListener('pointerover', this.#menuPointerEnterListener);
    this.addEventListener('pointerout', this.#menuPointerLeaveListener);
    document.addEventListener('click', this.#documentClickListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('focusin', this.#submenuHeightChangeListener);
    this.removeEventListener('focusout', this.#submenuHeightChangeListener);
    this.removeEventListener('pointerover', this.#submenuHeightChangeListener);
    this.removeEventListener('pointerout', this.#submenuHeightChangeListener);
    this.removeEventListener('pointerover', this.#menuPointerEnterListener);
    this.removeEventListener('pointerout', this.#menuPointerLeaveListener);
    document.removeEventListener('click', this.#documentClickListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cancelScheduledDeactivate();
    this.#completeClosingAnimation();
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #menuPointerEnterListener = () => {
    this.#cancelScheduledDeactivate();
  };

  /**
   * Close only after the pointer leaves the entire header menu, not when moving between its items.
   * @param {PointerEvent} event
   */
  #menuPointerLeaveListener = (event) => {
    const relatedTarget = event.relatedTarget;
    const overflowShadowRoot = this.refs.overflowMenu?.shadowRoot;

    if (
      relatedTarget instanceof Node &&
      (this.contains(relatedTarget) || overflowShadowRoot?.contains(relatedTarget))
    ) {
      return;
    }

    this.#scheduleDeactivate(this.#state.activeItem, true);
  };

  /**
   * Close an active dropdown when clicking anywhere outside the header menu.
   * @param {MouseEvent} event
   */
  #documentClickListener = (event) => {
    if (!this.#state.activeItem || event.composedPath().includes(this)) return;

    this.#deactivate(this.#state.activeItem, true, true);
  };

  /**
   * Shadow DOM overflow content needs its own outside-boundary check.
   * @param {PointerEvent} event
   */
  #overflowSubmenuListener = (event) => {
    const relatedTarget = event.relatedTarget;
    const overflowShadowRoot = this.refs.overflowMenu?.shadowRoot;

    if (
      relatedTarget instanceof Node &&
      (this.contains(relatedTarget) || overflowShadowRoot?.contains(relatedTarget))
    ) {
      return;
    }

    this.#scheduleDeactivate(this.#state.activeItem, true);
  };

  /**
   * Recalculate submenu height when hover-accordion panels expand or collapse.
   * @param {Event} event
   */
  #submenuHeightChangeListener = (event) => {
    if (!(event.target instanceof Element)) return;

    const submenu = event.target.closest('.menu-list__submenu');
    const megaMenu = submenu?.querySelector('.mega-menu--hover-accordion');
    if (!(submenu instanceof HTMLElement) || !(megaMenu instanceof HTMLElement)) return;
    if (!('active' in submenu.dataset)) return;

    if (event.type === 'pointerover' || event.type === 'focusin') {
      this.#setActiveAccordionItem(event.target, megaMenu);
    } else {
      this.#ensureActiveAccordionItem(megaMenu);
    }

    this.#scheduleSubmenuHeightUpdate(submenu);
  };

  /**
   * Activate the hovered accordion item. Items without child links intentionally leave the panel column empty.
   * @param {Element} target
   * @param {HTMLElement} megaMenu
   */
  #setActiveAccordionItem(target, megaMenu) {
    const item = target.closest('.mega-menu__accordion-item');
    if (!(item instanceof HTMLElement) || !megaMenu.contains(item)) {
      this.#ensureActiveAccordionItem(megaMenu);
      return;
    }

    const list = item.closest('.mega-menu__list--hover-accordion');
    if (!(list instanceof HTMLElement)) return;

    if (item.classList.contains('mega-menu__accordion-item--active')) return;

    list.querySelectorAll('.mega-menu__accordion-item--active').forEach((activeItem) => {
      activeItem.classList.remove('mega-menu__accordion-item--active');
    });
    item.classList.add('mega-menu__accordion-item--active');
  }

  /**
   * Reset the accordion for a newly opened submenu.
   * @param {HTMLElement} megaMenu
   */
  #resetActiveAccordionItem(megaMenu) {
    megaMenu.querySelectorAll('.mega-menu__accordion-item--active').forEach((activeItem) => {
      activeItem.classList.remove('mega-menu__accordion-item--active');
    });
    this.#ensureActiveAccordionItem(megaMenu);
  }

  /**
   * Keep one default panel open when the setting asks for the first group to expand.
   * @param {HTMLElement} megaMenu
   */
  #ensureActiveAccordionItem(megaMenu) {
    const list = megaMenu.querySelector('.mega-menu__list--hover-accordion');
    if (!(list instanceof HTMLElement)) return;

    if (list.querySelector('.mega-menu__accordion-item--active')) return;
    if (megaMenu.dataset.expandFirstGroup !== 'true') return;

    const firstGroup = list.querySelector('.mega-menu__accordion-item--first-group');
    if (firstGroup instanceof HTMLElement) {
      firstGroup.classList.add('mega-menu__accordion-item--active');
    }
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    const pointerInsideMenu = event.composedPath().includes(this);
    if (!pointerInsideMenu) {
      if (!this.#deactivateTimer) {
        this.#scheduleDeactivate(activeLink, true);
      }
      return;
    }

    this.#cancelScheduledDeactivate();
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu?.contains(target)) return;

      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';
    const hasDropdown = Boolean(findSubmenu(item)) || !isDefaultSlot;

    this.#cancelScheduledDeactivate();

    // Pointer-opened dropdowns stay active until another dropdown is hovered.
    // Plain top-level links must not replace the active dropdown with a zero-height menu state.
    if (event.type === 'pointerenter' && !hasDropdown) return;

    this.#completeClosingAnimation();

    if (item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const previouslyActiveSubmenu = findSubmenu(previouslyActiveItem);
      if (previouslyActiveSubmenu) {
        delete previouslyActiveSubmenu.dataset.active;
        previouslyActiveSubmenu.style.removeProperty('--submenu-pinned-height');
      }
    }

    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
    this.#activeSubmenuMaxHeight = 0;
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    let hoverAccordionMenu = null;

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      submenu.style.removeProperty('--submenu-pinned-height');

      hoverAccordionMenu = submenu.querySelector('.mega-menu--hover-accordion');
      if (hoverAccordionMenu instanceof HTMLElement) {
        this.#resetActiveAccordionItem(hoverAccordionMenu);
      }

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#updateSubmenuHeight(submenu);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      this.#submenuMutationObserverTimer = setTimeout(() => {
        this.#submenuMutationObserverTimer = undefined;
        this.#cleanupMutationObserver();
      }, 500);

      this.#observeSubmenuHeight(submenu);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    if (submenu?.classList.contains('menu-list__submenu--hover-accordion')) {
      this.#activeSubmenuMaxHeight = Math.max(this.#activeSubmenuMaxHeight, finalHeight);
      submenu.style.setProperty('--submenu-pinned-height', `${this.#activeSubmenuMaxHeight}px`);
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    if (hoverAccordionMenu instanceof HTMLElement) {
      this.#scheduleSubmenuHeightUpdate(submenu);
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const hasOpenDropdown = Boolean(menu) || this.dataset.overflowExpanded === 'true';
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    // Pointer-opened dropdowns are intentionally sticky. A different dropdown's pointerenter
    // event will switch the active menu; leaving the current menu or hovering a plain link will not close it.
    const shouldKeepDropdownOpen = event.type === 'pointerleave' && hasOpenDropdown;

    if (shouldKeepDropdownOpen || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (!shouldKeepDropdownOpen) {
        this.#cancelScheduledDeactivate();
      }
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#scheduleDeactivate(this.#state.activeItem, event.type === 'pointerleave');
  }

  /**
   * Deactivate the active item after pointer and hover state have settled.
   * @param {HTMLElement | null} item
   * @param {boolean} [ignoreHoverState]
   */
  #scheduleDeactivate(item, ignoreHoverState = false) {
    if (!item) return;

    this.#cancelScheduledDeactivate();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate(item, ignoreHoverState);
    }, SUBMENU_CLOSE_DELAY);
  }

  #cancelScheduledDeactivate() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  /**
   * Deactivate the active item.
   * @param {HTMLElement | null} [item]
   * @param {boolean} [ignoreHoverState]
   * @param {boolean} [forceClose]
   */
  #deactivate = (item = this.#state.activeItem, ignoreHoverState = false, forceClose = false) => {
    if (!item || item != this.#state.activeItem) return;

    this.#cancelScheduledDeactivate();

    const submenu = findSubmenu(item);
    const closingSubmenu = submenu ?? (this.dataset.overflowExpanded === 'true' ? this.overflowMenu : null);

    // Keep the menu available for keyboard users even when their pointer has left the header.
    if (!forceClose && submenu?.contains(document.activeElement)) return;

    // Focus-driven closes should still respect a pointer that remains inside the menu.
    // Pointerleave closes skip this check because :hover can be stale during the event frame.
    if (
      !forceClose &&
      !ignoreHoverState &&
      (submenu?.matches(':hover') || this.overflowListHovered || this.overflowMenu?.matches(':hover'))
    ) {
      return;
    }

    // Keep the content visible-but-fading while the header and clip path finish closing.
    // This prevents a visibility snap when the pointer quickly re-enters the same menu.
    this.#startClosingAnimation(closingSubmenu);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.#activeSubmenuMaxHeight = 0;

    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
  };

  /**
   * Preserve the closing surface until its CSS transition finishes.
   * @param {HTMLElement | null} submenu
   */
  #startClosingAnimation(submenu) {
    this.#completeClosingAnimation();

    this.#closingSubmenu = submenu;
    this.dataset.closing = 'true';
    if (submenu) {
      submenu.dataset.closing = '';
    }

    const animationDuration = this.#getSubmenuAnimationDuration();
    this.#closeAnimationTimer = setTimeout(
      this.#completeClosingAnimation,
      animationDuration + SUBMENU_ANIMATION_CLEANUP_BUFFER
    );
  }

  /**
   * Remove visual closing state after the CSS transition, or immediately when reopening.
   */
  #completeClosingAnimation = () => {
    const hadClosingState =
      this.#closingSubmenu !== null ||
      this.#closeAnimationTimer !== undefined ||
      this.dataset.closing === 'true';

    clearTimeout(this.#closeAnimationTimer);
    this.#closeAnimationTimer = undefined;

    if (this.#closingSubmenu) {
      delete this.#closingSubmenu.dataset.closing;
      delete this.#closingSubmenu.dataset.active;
      this.#closingSubmenu.style.removeProperty('--submenu-pinned-height');
    }

    this.#closingSubmenu = null;
    delete this.dataset.closing;

    if (hadClosingState) {
      this.dataset.overflowExpanded = 'false';
    }
  };

  /**
   * Read the shared CSS animation duration so visual and JavaScript cleanup stay synchronized.
   * @returns {number}
   */
  #getSubmenuAnimationDuration() {
    const cssDuration = getComputedStyle(this).getPropertyValue('--submenu-animation-speed').trim();
    const value = Number.parseFloat(cssDuration);

    if (!Number.isFinite(value)) return DEFAULT_SUBMENU_ANIMATION_DURATION;
    if (cssDuration.endsWith('ms')) return value;
    if (cssDuration.endsWith('s')) return value * 1000;

    return DEFAULT_SUBMENU_ANIMATION_DURATION;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes can affect layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Schedule height measurement after CSS hover/focus styles and transitions have started.
   * @param {HTMLElement} submenu
   */
  #scheduleSubmenuHeightUpdate(submenu) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#updateSubmenuHeight(submenu));
    });

    setTimeout(() => this.#updateSubmenuHeight(submenu), 400);
  }

  /**
   * Update the header open height from the active submenu.
   * @param {HTMLElement} submenu
   */
  #updateSubmenuHeight(submenu) {
    if (!this.headerComponent || !('active' in submenu.dataset)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const activeSubmenu =
      findSubmenu(activeItem) ?? (this.dataset.overflowExpanded === 'true' ? this.overflowMenu : null);
    if (submenu !== activeSubmenu) return;

    let submenuHeight = submenu.offsetHeight || 0;

    if (submenu.classList.contains('menu-list__submenu--hover-accordion')) {
      this.#activeSubmenuMaxHeight = Math.max(this.#activeSubmenuMaxHeight, submenuHeight);
      submenuHeight = this.#activeSubmenuMaxHeight;
      submenu.style.setProperty('--submenu-pinned-height', `${submenuHeight}px`);
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    clearTimeout(this.#submenuMutationObserverTimer);
    this.#submenuMutationObserverTimer = undefined;
  }

  /**
   * Keep the active submenu height synchronized for the full open session.
   * @param {HTMLElement} submenu
   */
  #observeSubmenuHeight(submenu) {
    this.#cleanupResizeObserver();
    this.#submenuResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.#updateSubmenuHeight(submenu));
    });
    this.#submenuResizeObserver.observe(submenu);
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
